import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { formatDate, formatDateTime } from '@utils/dateUtils'
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Chip,
  Tabs,
  Tab,
  Typography,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Autocomplete,
} from '@mui/material'
import { Add, Edit, Delete, PlayArrow, Dashboard, ViewTimeline, AttachMoney, Schedule, Print, FilterList, Clear } from '@mui/icons-material'
import { PageHeader, Button, DataTable, StatusChip, Card, Modal, LoadingSpinner, ExportButtons } from '@components/common'
import { toast } from 'react-toastify'
import api from '@services/api'

const FREQUENCY_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Bi-Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'semi_annual', label: 'Semi-Annual' },
  { value: 'annual', label: 'Annual' },
]

const STATUS_OPTIONS = [
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'skipped', label: 'Skipped' },
]

const TARGET_TYPE_OPTIONS = [
  { value: 'asset_category', label: 'Asset Category' },
  { value: 'vehicle', label: 'Vehicle' },
]

const VEHICLE_TYPE_OPTIONS = [
  { value: 'ambulance', label: 'Ambulance' },
  { value: 'bus', label: 'Bus' },
  { value: 'car', label: 'Car' },
  { value: 'motorcycle', label: 'Motorcycle' },
  { value: 'pickup', label: 'Pickup' },
  { value: 'sedan', label: 'Sedan' },
  { value: 'suv', label: 'SUV' },
  { value: 'truck', label: 'Truck' },
  { value: 'van', label: 'Van' },
  { value: 'other', label: 'Other' },
]

// PM Templates Tab
const PMTemplatesTab = () => {
  const [templates, setTemplates] = useState([])
  const [categories, setCategories] = useState([])
  const [vendors, setVendors] = useState([])
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    target_type: 'asset_category',
    category: '',
    vehicle_type: '',
    meter_interval: '',
    frequency: 'monthly',
    frequency_days: 30,
    frequency_per_year: 4,
    maintenance_provider: 'internal',
    service_vendor: '',
    estimated_duration_hours: 1,
    required_technicians: 1,
    description: '',
    is_active: true,
    labor_rate_per_hour: 0,
    estimated_parts: [],
  })
  const [newPart, setNewPart] = useState({ part_name: '', quantity: 1, estimated_unit_cost: 0 })

  const fetchData = async () => {
    try {
      setLoading(true)
      const [templatesRes, categoriesRes, vendorsRes] = await Promise.all([
        api.get('/maintenance/pm-templates/', { params: { ordering: '-created_at' } }),
        api.get('/assets/categories/'),
        api.get('/vendors/').catch(() => ({ data: [] })),
      ])
      setTemplates(templatesRes.data.results || templatesRes.data)
      setCategories(categoriesRes.data.results || categoriesRes.data)
      setVendors(vendorsRes.data.results || vendorsRes.data || [])
    } catch (error) {
      console.error('Error fetching data:', error)
      toast.error('Failed to load PM templates')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const handleAdd = () => {
    setSelectedTemplate(null)
    setFormData({
      name: '',
      target_type: 'asset_category',
      category: '',
      vehicle_type: '',
      meter_interval: '',
      frequency: 'monthly',
      frequency_days: 30,
      frequency_per_year: 4,
      maintenance_provider: 'internal',
      service_vendor: '',
      estimated_duration_hours: 1,
      required_technicians: 1,
      description: '',
      is_active: true,
      labor_rate_per_hour: 0,
      estimated_parts: [],
    })
    setNewPart({ part_name: '', quantity: 1, estimated_unit_cost: 0 })
    setFormOpen(true)
  }

  const handleEdit = (template) => {
    setSelectedTemplate(template)
    // Handle both nested objects and direct IDs
    const categoryId = template.category?.id || template.category || ''
    const serviceVendorId = template.service_vendor?.id || template.service_vendor || ''
    setFormData({
      name: template.name,
      target_type: template.target_type || 'asset_category',
      category: categoryId,
      vehicle_type: template.vehicle_type || '',
      meter_interval: template.meter_interval ?? '',
      frequency: template.frequency,
      frequency_days: template.frequency_days,
      frequency_per_year: template.frequency_per_year || 4,
      maintenance_provider: template.maintenance_provider || 'internal',
      service_vendor: serviceVendorId,
      estimated_duration_hours: template.estimated_duration_hours,
      required_technicians: template.required_technicians || 1,
      description: template.description || '',
      is_active: template.is_active,
      labor_rate_per_hour: template.labor_rate_per_hour || 0,
      estimated_parts: template.estimated_parts || [],
    })
    setNewPart({ part_name: '', quantity: 1, estimated_unit_cost: 0 })
    setFormOpen(true)
  }

  const handleAddPart = () => {
    if (newPart.part_name && newPart.quantity > 0) {
      setFormData({
        ...formData,
        estimated_parts: [...formData.estimated_parts, { ...newPart }],
      })
      setNewPart({ part_name: '', quantity: 1, estimated_unit_cost: 0 })
    }
  }

  const handleRemovePart = (index) => {
    setFormData({
      ...formData,
      estimated_parts: formData.estimated_parts.filter((_, i) => i !== index),
    })
  }

  // Calculate estimated costs
  const estimatedLaborCost = (formData.estimated_duration_hours || 0) * (formData.labor_rate_per_hour || 0)
  const estimatedPartsCost = (formData.estimated_parts || []).reduce(
    (sum, p) => sum + (p.quantity || 0) * (p.estimated_unit_cost || 0),
    0
  )
  const totalEstimatedCost = estimatedLaborCost + estimatedPartsCost

  const handleSave = async () => {
    setSaving(true)
    try {
      const isVehicle = formData.target_type === 'vehicle'
      const payload = {
        ...formData,
        category: isVehicle ? null : (formData.category || null),
        vehicle_type: isVehicle ? (formData.vehicle_type || '') : '',
        meter_interval: formData.meter_interval === '' ? null : parseInt(formData.meter_interval),
        service_vendor: formData.service_vendor || null,
      }
      if (selectedTemplate) {
        await api.patch(`/maintenance/pm-templates/${selectedTemplate.id}/`, payload)
        toast.success('Template updated successfully')
      } else {
        await api.post('/maintenance/pm-templates/', payload)
        toast.success('Template created successfully')
      }
      setFormOpen(false)
      fetchData()
    } catch (error) {
      console.error('Error saving template:', error)
      toast.error(error.response?.data?.error || 'Failed to save template')
    } finally {
      setSaving(false)
    }
  }

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '-'
    return new Intl.NumberFormat('en-UG', {
      style: 'currency',
      currency: 'UGX',
      minimumFractionDigits: 0,
    }).format(value)
  }

  const columns = [
    { field: 'name', headerName: 'Template Name', sortable: true },
    {
      field: 'category_name',
      headerName: 'Applies To',
      renderCell: (v, row) => row.target_type === 'vehicle'
        ? `Vehicle${row.vehicle_type_display ? ` (${row.vehicle_type_display})` : 's (all)'}`
        : (v || row.category?.name || '-'),
    },
    {
      field: 'frequency',
      headerName: 'Frequency',
      renderCell: (v) => <Chip label={v?.replace('_', ' ')} size="small" sx={{ textTransform: 'capitalize' }} />,
    },
    {
      field: 'meter_interval',
      headerName: 'Meter Interval',
      renderCell: (v, row) => v ? `${v} ${row.target_type === 'vehicle' ? 'km' : 'hrs'}` : '-',
    },
    { field: 'frequency_per_year', headerName: '# Times/Year', renderCell: (v) => v || '-' },
    { field: 'estimated_duration_hours', headerName: 'Est. Hours' },
    { field: 'required_technicians', headerName: 'Technicians', renderCell: (v) => v || 1 },
    {
      field: 'maintenance_provider_display',
      headerName: 'Action By',
      renderCell: (v) => v || 'In-house BME',
    },
    {
      field: 'total_estimated_cost',
      headerName: 'Est. Cost',
      renderCell: (v) => formatCurrency(v),
    },
    {
      field: 'is_active',
      headerName: 'Status',
      renderCell: (value) => <StatusChip status={value ? 'active' : 'inactive'} />,
    },
    {
      field: 'actions',
      headerName: '',
      renderCell: (_, row) => (
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEdit(row) }}>
          <Edit fontSize="small" />
        </IconButton>
      ),
    },
  ]

  return (
    <>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button startIcon={<Add />} onClick={handleAdd}>Add Template</Button>
      </Box>
      <DataTable
        columns={columns}
        data={templates}
        loading={loading}
        searchPlaceholder="Search templates..."
        emptyMessage="No PM templates found"
      />

      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={selectedTemplate ? 'Edit PM Template' : 'Add PM Template'}
        maxWidth="md"
        actions={
          <>
            <Button variant="outlined" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} loading={saving}>{selectedTemplate ? 'Update' : 'Create'}</Button>
          </>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Template Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Applies To</InputLabel>
              <Select
                value={formData.target_type}
                onChange={(e) => setFormData({ ...formData, target_type: e.target.value })}
                label="Applies To"
              >
                {TARGET_TYPE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {formData.target_type === 'vehicle' ? (
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Vehicle Type</InputLabel>
                <Select
                  value={formData.vehicle_type}
                  onChange={(e) => setFormData({ ...formData, vehicle_type: e.target.value })}
                  label="Vehicle Type"
                >
                  <MenuItem value="">All Vehicles</MenuItem>
                  {VEHICLE_TYPE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          ) : (
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  label="Category"
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {categories.map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={formData.target_type === 'vehicle' ? 'Meter Interval (km)' : 'Meter Interval (machine-hours)'}
              type="number"
              value={formData.meter_interval}
              onChange={(e) => setFormData({ ...formData, meter_interval: e.target.value })}
              inputProps={{ min: 0 }}
              helperText="Leave blank for calendar-only scheduling"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Frequency</InputLabel>
              <Select
                value={formData.frequency}
                onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                label="Frequency"
              >
                {FREQUENCY_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Frequency Days"
              type="number"
              value={formData.frequency_days}
              onChange={(e) => setFormData({ ...formData, frequency_days: parseInt(e.target.value) })}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="# Times per Year"
              type="number"
              value={formData.frequency_per_year}
              onChange={(e) => setFormData({ ...formData, frequency_per_year: parseInt(e.target.value) || 4 })}
              inputProps={{ min: 1, max: 365 }}
              helperText="e.g., 2 or 4"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Maintenance Provider</InputLabel>
              <Select
                value={formData.maintenance_provider}
                onChange={(e) => setFormData({ ...formData, maintenance_provider: e.target.value })}
                label="Maintenance Provider"
              >
                <MenuItem value="internal">In-house BME</MenuItem>
                <MenuItem value="vendor">External Vendor</MenuItem>
                <MenuItem value="contract">Service Contract</MenuItem>
                <MenuItem value="tbd">To Be Determined</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {(formData.maintenance_provider === 'vendor' || formData.maintenance_provider === 'contract') && (
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Service Vendor</InputLabel>
                <Select
                  value={formData.service_vendor}
                  onChange={(e) => setFormData({ ...formData, service_vendor: e.target.value })}
                  label="Service Vendor"
                >
                  <MenuItem value="">Select Vendor</MenuItem>
                  {vendors.map((v) => (
                    <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Estimated Hours"
              type="number"
              value={formData.estimated_duration_hours}
              onChange={(e) => setFormData({ ...formData, estimated_duration_hours: parseFloat(e.target.value) })}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Technicians Required"
              type="number"
              value={formData.required_technicians}
              onChange={(e) => setFormData({ ...formData, required_technicians: parseInt(e.target.value) || 1 })}
              inputProps={{ min: 1 }}
              helperText="Number of technicians needed"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={2}
            />
          </Grid>

          {/* Cost Estimation Section */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mt: 1, mb: 1, fontWeight: 600 }}>
              Cost Estimation
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Labor Rate per Hour"
              type="number"
              value={formData.labor_rate_per_hour}
              onChange={(e) => setFormData({ ...formData, labor_rate_per_hour: parseFloat(e.target.value) || 0 })}
              InputProps={{
                startAdornment: <InputAdornment position="start">UGX</InputAdornment>,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">Est. Labor Cost</Typography>
              <Typography variant="body1" fontWeight={600}>
                {formatCurrency(estimatedLaborCost)}
              </Typography>
            </Box>
          </Grid>

          {/* Parts Section */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mt: 1, mb: 1, fontWeight: 600 }}>
              Required Parts
            </Typography>
          </Grid>

          <Grid item xs={12} sm={5}>
            <TextField
              fullWidth
              size="small"
              label="Part Name"
              value={newPart.part_name}
              onChange={(e) => setNewPart({ ...newPart, part_name: e.target.value })}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField
              fullWidth
              size="small"
              label="Qty"
              type="number"
              value={newPart.quantity}
              onChange={(e) => setNewPart({ ...newPart, quantity: parseInt(e.target.value) || 1 })}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              size="small"
              label="Unit Cost"
              type="number"
              value={newPart.estimated_unit_cost}
              onChange={(e) => setNewPart({ ...newPart, estimated_unit_cost: parseFloat(e.target.value) || 0 })}
              InputProps={{
                startAdornment: <InputAdornment position="start">UGX</InputAdornment>,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button size="small" onClick={handleAddPart} disabled={!newPart.part_name}>
              Add
            </Button>
          </Grid>

          {/* Parts List */}
          {formData.estimated_parts && formData.estimated_parts.length > 0 && (
            <Grid item xs={12}>
              <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
                <Box sx={{ p: 1, backgroundColor: '#f5f5f5', display: 'flex', gap: 1 }}>
                  <Typography variant="caption" sx={{ flex: 3, fontWeight: 600 }}>Part</Typography>
                  <Typography variant="caption" sx={{ flex: 1, fontWeight: 600 }}>Qty</Typography>
                  <Typography variant="caption" sx={{ flex: 2, fontWeight: 600 }}>Unit Cost</Typography>
                  <Typography variant="caption" sx={{ flex: 2, fontWeight: 600 }}>Total</Typography>
                  <Typography variant="caption" sx={{ width: 40 }}></Typography>
                </Box>
                {formData.estimated_parts.map((part, index) => (
                  <Box key={index} sx={{ p: 1, display: 'flex', gap: 1, borderTop: '1px solid #e0e0e0' }}>
                    <Typography variant="body2" sx={{ flex: 3 }}>{part.part_name}</Typography>
                    <Typography variant="body2" sx={{ flex: 1 }}>{part.quantity}</Typography>
                    <Typography variant="body2" sx={{ flex: 2 }}>{formatCurrency(part.estimated_unit_cost)}</Typography>
                    <Typography variant="body2" sx={{ flex: 2 }}>{formatCurrency(part.quantity * part.estimated_unit_cost)}</Typography>
                    <IconButton size="small" onClick={() => handleRemovePart(index)} sx={{ width: 40 }}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </Grid>
          )}

          {/* Cost Summary */}
          <Grid item xs={12}>
            <Box sx={{ p: 2, backgroundColor: '#e3f2fd', borderRadius: 1, display: 'flex', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Parts Cost</Typography>
                <Typography variant="body2" fontWeight={500}>{formatCurrency(estimatedPartsCost)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Labor Cost</Typography>
                <Typography variant="body2" fontWeight={500}>{formatCurrency(estimatedLaborCost)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Total Est. Cost</Typography>
                <Typography variant="h6" fontWeight={700} color="primary.main">{formatCurrency(totalEstimatedCost)}</Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Modal>
    </>
  )
}

// PM Schedules Tab
const PMSchedulesTab = () => {
  const navigate = useNavigate()
  const [schedules, setSchedules] = useState([])
  const [assets, setAssets] = useState([])
  const [templates, setTemplates] = useState([])
  const [users, setUsers] = useState([])
  const [vendors, setVendors] = useState([])
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [selectedSchedule, setSelectedSchedule] = useState(null)
  const [saving, setSaving] = useState(false)
  const [showFilters, setShowFilters] = useState(false)

  // Filter state
  const [filters, setFilters] = useState({
    status: '',
    template: '',
  })

  const [formData, setFormData] = useState({
    asset: '',
    template: '',
    scheduled_date: '',
    due_date: '',
    scheduled_week: '',
    status: 'scheduled',
    assigned_to: '',
    maintenance_provider: 'internal',
    service_vendor: '',
    notes: '',
  })
  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null,
    confirmText: 'Confirm',
    confirmColor: 'primary',
    loading: false,
  })

  const fetchData = async () => {
    try {
      setLoading(true)
      const params = { ordering: '-created_at' }
      if (filters.status) params.status = filters.status
      if (filters.template) params.template = filters.template

      const [schedulesRes, assetsRes, templatesRes, usersRes, vendorsRes] = await Promise.all([
        api.get('/maintenance/pm-schedules/', { params }),
        api.get('/assets/assets/', { params: { ordering: '-created_at' } }),
        api.get('/maintenance/pm-templates/', { params: { ordering: '-created_at' } }),
        api.get('/common/users/', { params: { page_size: 100, ordering: 'first_name' } }).catch(() => ({ data: [] })),
        api.get('/vendors/').catch(() => ({ data: [] })),
      ])
      setSchedules(schedulesRes.data.results || schedulesRes.data)
      setAssets(assetsRes.data.results || assetsRes.data)
      setTemplates(templatesRes.data.results || templatesRes.data)
      // All users can be assigned to a PM schedule
      const usersData = usersRes.data.results || usersRes.data || []
      setUsers(usersData)
      setVendors(vendorsRes.data.results || vendorsRes.data || [])
    } catch (error) {
      console.error('Error fetching data:', error)
      toast.error('Failed to load PM schedules data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [filters])

  // Export columns definition
  const exportColumns = useMemo(() => [
    { field: 'asset_name', headerName: 'Asset Name', renderValue: (v, row) => v || row.asset?.name || '-' },
    { field: 'department_name', headerName: 'Department', renderValue: (v) => v || '-' },
    { field: 'facility_name', headerName: 'Facility', renderValue: (v) => v || '-' },
    { field: 'unit_count', headerName: '# of Units', renderValue: (v) => v || 1 },
    { field: 'frequency_per_year', headerName: '# of Times/Year', renderValue: (v) => v || '-' },
    { field: 'template_name', headerName: 'PM Template', renderValue: (v, row) => v || row.template?.name || '-' },
    { field: 'scheduled_date', headerName: 'Scheduled', renderValue: (v) => v ? formatDate(v) : '-' },
    { field: 'due_date', headerName: 'Due Date', renderValue: (v) => v ? formatDate(v) : '-' },
    { field: 'assigned_to_name', headerName: 'Assigned To', renderValue: (v, row) => v || row.assigned_to?.username || 'Unassigned' },
    { field: 'maintenance_provider_display', headerName: 'Action By', renderValue: (v) => v || 'In-house BME' },
    { field: 'status', headerName: 'Status', renderValue: (v) => STATUS_OPTIONS.find(o => o.value === v)?.label || v },
    { field: 'work_order_number', headerName: 'Work Order' },
  ], [])

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }))
  }

  const clearFilters = () => {
    setFilters({ status: '', template: '' })
  }

  const activeFilterCount = Object.values(filters).filter(v => v !== '').length

  const handleAdd = () => {
    setSelectedSchedule(null)
    const today = new Date().toISOString().split('T')[0]
    setFormData({
      asset: '',
      template: '',
      scheduled_date: today,
      due_date: today,
      scheduled_week: '',
      status: 'scheduled',
      assigned_to: '',
      maintenance_provider: 'internal',
      service_vendor: '',
      notes: '',
    })
    setFormOpen(true)
  }

  const handleEdit = (schedule) => {
    setSelectedSchedule(schedule)
    // Handle both nested objects and direct IDs (list vs detail serializer)
    const assetId = schedule.asset_detail?.id || schedule.asset?.id || schedule.asset || ''
    const templateId = schedule.template_detail?.id || schedule.template?.id || schedule.template || ''
    const assignedToId = schedule.assigned_to_detail?.id || schedule.assigned_to?.id || schedule.assigned_to || ''
    const serviceVendorId = schedule.service_vendor_detail?.id || schedule.service_vendor?.id || schedule.service_vendor || ''

    setFormData({
      asset: assetId,
      template: templateId,
      scheduled_date: schedule.scheduled_date || '',
      due_date: schedule.due_date || '',
      scheduled_week: schedule.scheduled_week || '',
      status: schedule.status,
      assigned_to: assignedToId,
      maintenance_provider: schedule.maintenance_provider || 'internal',
      service_vendor: serviceVendorId,
      notes: schedule.notes || '',
    })
    setFormOpen(true)
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const payload = {
        ...formData,
        asset: formData.asset || null,
        template: formData.template || null,
        assigned_to: formData.assigned_to || null,
        service_vendor: formData.service_vendor || null,
        scheduled_week: formData.scheduled_week || null,
      }
      if (selectedSchedule) {
        await api.patch(`/maintenance/pm-schedules/${selectedSchedule.id}/`, payload)
        toast.success('Schedule updated successfully')
      } else {
        await api.post('/maintenance/pm-schedules/', payload)
        toast.success('PM scheduled successfully')
      }
      setFormOpen(false)
      fetchData()
    } catch (error) {
      console.error('Error saving schedule:', error)
      toast.error(error.response?.data?.error || 'Failed to save schedule')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = (schedule) => {
    setConfirmModal({
      open: true,
      title: 'Delete PM Schedule',
      message: `Are you sure you want to delete the PM schedule for "${schedule.asset_name || schedule.asset?.name}"?`,
      confirmText: 'Delete',
      confirmColor: 'error',
      loading: false,
      onConfirm: async () => {
        setConfirmModal((prev) => ({ ...prev, loading: true }))
        try {
          await api.delete(`/maintenance/pm-schedules/${schedule.id}/`)
          toast.success('Schedule deleted')
          setConfirmModal({ open: false, title: '', message: '', onConfirm: null, confirmText: 'Confirm', confirmColor: 'primary', loading: false })
          fetchData()
        } catch (error) {
          console.error('Error deleting schedule:', error)
          toast.error('Failed to delete schedule')
          setConfirmModal((prev) => ({ ...prev, loading: false }))
        }
      },
    })
  }

  const handleStartPM = (schedule) => {
    // Starting a PM now creates a work order - this is the proper workflow
    if (schedule.work_order) {
      toast.info('Work order already exists for this PM')
      return
    }

    const assetName = schedule.asset_name || schedule.asset?.name
    const templateName = schedule.template_name || schedule.template?.name || 'N/A'

    setConfirmModal({
      open: true,
      title: 'Start Preventive Maintenance',
      message: `Start this PM and create a work order?\n\nAsset: ${assetName}\nTemplate: ${templateName}`,
      confirmText: 'Start PM',
      confirmColor: 'success',
      loading: false,
      onConfirm: async () => {
        setConfirmModal((prev) => ({ ...prev, loading: true }))
        try {
          const response = await api.post(`/maintenance/pm-schedules/${schedule.id}/create_work_order/`)
          toast.success(response.data.message || 'PM started - Work order created')
          setConfirmModal({ open: false, title: '', message: '', onConfirm: null, confirmText: 'Confirm', confirmColor: 'primary', loading: false })
          fetchData()
        } catch (error) {
          console.error('Error starting PM:', error)
          const errorMsg = error.response?.data?.error || 'Failed to start PM'
          toast.error(errorMsg)
          setConfirmModal((prev) => ({ ...prev, loading: false }))
        }
      },
    })
  }

  const columns = [
    {
      field: 'asset_name',
      headerName: 'Asset / Vehicle',
      sortable: true,
      renderCell: (v, row) =>
        v || row.asset?.name || row.vehicle_name || row.vehicle?.name || '-',
    },
    {
      field: 'trigger_reason',
      headerName: 'Trigger',
      width: 110,
      renderCell: (v, row) => row.trigger_reason_display || v || 'Calendar',
    },
    {
      field: 'department_name',
      headerName: 'Department',
      renderCell: (v) => v || '-',
    },
    {
      field: 'unit_count',
      headerName: '# Units',
      width: 80,
      renderCell: (v) => v || 1,
    },
    {
      field: 'frequency_per_year',
      headerName: '# Times',
      width: 80,
      renderCell: (v) => v || '-',
    },
    {
      field: 'scheduled_date',
      headerName: 'Scheduled',
      renderCell: (v) => v ? formatDate(v) : '-',
      sortable: true,
    },
    {
      field: 'due_date',
      headerName: 'Due Date',
      renderCell: (v) => v ? formatDate(v) : '-',
      sortable: true,
    },
    {
      field: 'maintenance_provider_display',
      headerName: 'Action By',
      renderCell: (v) => v || 'In-house BME',
    },
    {
      field: 'status',
      headerName: 'Status',
      renderCell: (value) => <StatusChip status={value} />,
    },
    {
      field: 'work_order',
      headerName: 'Work Order',
      renderCell: (v, row) => {
        const woNumber = row.work_order_number || row.work_order?.work_order_number
        if (woNumber) {
          return (
            <Chip
              size="small"
              label={woNumber}
              color="primary"
              variant="outlined"
              onClick={(e) => {
                e.stopPropagation()
                const woId = row.work_order?.id || row.work_order
                if (woId) navigate(`/work-orders/${woId}`)
              }}
              sx={{ cursor: 'pointer' }}
            />
          )
        }
        return <Typography variant="caption" color="text.secondary">-</Typography>
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 150,
      renderCell: (_, row) => (
        <Box display="flex" gap={0.5}>
          {(row.status === 'scheduled' || row.status === 'overdue') && !row.work_order && (
            <IconButton
              size="small"
              color="success"
              onClick={(e) => { e.stopPropagation(); handleStartPM(row) }}
              title="Start PM (Create Work Order)"
            >
              <PlayArrow fontSize="small" />
            </IconButton>
          )}
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEdit(row) }}>
            <Edit fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDelete(row) }}>
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ]

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box />
        <Stack direction="row" spacing={1}>
          <ExportButtons
            data={schedules}
            columns={exportColumns}
            title="PM Schedules"
            filename="pm_schedules"
            filters={{
              Status: filters.status ? STATUS_OPTIONS.find(o => o.value === filters.status)?.label : null,
              Template: filters.template ? templates.find(t => t.id === parseInt(filters.template))?.name : null,
            }}
          />
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            onClick={() => setShowFilters(!showFilters)}
            color={activeFilterCount > 0 ? 'primary' : 'inherit'}
          >
            Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
          </Button>
          <Button startIcon={<Add />} onClick={handleAdd}>Schedule PM</Button>
        </Stack>
      </Box>

      {/* Filters Panel */}
      {showFilters && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">All</MenuItem>
                  {STATUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Template</InputLabel>
                <Select
                  value={filters.template}
                  onChange={(e) => handleFilterChange('template', e.target.value)}
                  label="Template"
                >
                  <MenuItem value="">All</MenuItem>
                  {templates.map((t) => (
                    <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                variant="text"
                startIcon={<Clear />}
                onClick={clearFilters}
                disabled={activeFilterCount === 0}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </Box>
      )}

      <DataTable
        columns={columns}
        data={schedules}
        loading={loading}
        searchPlaceholder="Search schedules..."
        emptyMessage="No PM schedules found"
      />

      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={selectedSchedule ? 'Edit PM Schedule' : 'Schedule Preventive Maintenance'}
        maxWidth="sm"
        actions={
          <>
            <Button variant="outlined" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} loading={saving}>{selectedSchedule ? 'Update' : 'Schedule'}</Button>
          </>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth required>
              <InputLabel>Asset</InputLabel>
              <Select
                value={formData.asset}
                onChange={(e) => setFormData({ ...formData, asset: e.target.value })}
                label="Asset"
              >
                {assets.map((a) => (
                  <MenuItem key={a.id} value={a.id}>{a.name} ({a.asset_tag})</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>PM Template</InputLabel>
              <Select
                value={formData.template}
                onChange={(e) => setFormData({ ...formData, template: e.target.value })}
                label="PM Template"
              >
                <MenuItem value="">None</MenuItem>
                {templates.map((t) => (
                  <MenuItem key={t.id} value={t.id}>{t.name} ({t.frequency})</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Scheduled Date"
              type="date"
              value={formData.scheduled_date}
              onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Due Date"
              type="date"
              value={formData.due_date}
              onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={users}
              getOptionLabel={(u) => {
                const name = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username || 'Unknown'
                return u.email ? `${name} (${u.email})` : name
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={users.find((u) => u.id === formData.assigned_to) || null}
              onChange={(e, newValue) => setFormData({ ...formData, assigned_to: newValue?.id || '' })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Assigned To"
                  placeholder="Search by name or email"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                label="Status"
              >
                {STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Scheduled Week"
              type="number"
              value={formData.scheduled_week}
              onChange={(e) => setFormData({ ...formData, scheduled_week: e.target.value })}
              inputProps={{ min: 1, max: 4 }}
              helperText="Week of month (1-4)"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Maintenance Provider</InputLabel>
              <Select
                value={formData.maintenance_provider}
                onChange={(e) => setFormData({ ...formData, maintenance_provider: e.target.value })}
                label="Maintenance Provider"
              >
                <MenuItem value="internal">In-house BME</MenuItem>
                <MenuItem value="vendor">External Vendor</MenuItem>
                <MenuItem value="contract">Service Contract</MenuItem>
                <MenuItem value="tbd">To Be Determined</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {(formData.maintenance_provider === 'vendor' || formData.maintenance_provider === 'contract') && (
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Service Vendor</InputLabel>
                <Select
                  value={formData.service_vendor}
                  onChange={(e) => setFormData({ ...formData, service_vendor: e.target.value })}
                  label="Service Vendor"
                >
                  <MenuItem value="">Select Vendor</MenuItem>
                  {vendors.map((v) => (
                    <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              multiline
              rows={2}
            />
          </Grid>
        </Grid>
      </Modal>

      {/* Confirmation Modal */}
      <Modal
        open={confirmModal.open}
        onClose={() => !confirmModal.loading && setConfirmModal({ ...confirmModal, open: false })}
        title={confirmModal.title}
        maxWidth="xs"
        actions={
          <>
            <Button
              variant="outlined"
              onClick={() => setConfirmModal({ ...confirmModal, open: false })}
              disabled={confirmModal.loading}
            >
              Cancel
            </Button>
            <Button
              color={confirmModal.confirmColor}
              onClick={confirmModal.onConfirm}
              loading={confirmModal.loading}
            >
              {confirmModal.confirmText}
            </Button>
          </>
        }
      >
        <Typography sx={{ whiteSpace: 'pre-line' }}>
          {confirmModal.message}
        </Typography>
      </Modal>
    </>
  )
}

// Fiscal year months (June to May)
const FISCAL_MONTHS = ['Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar', 'Apr', 'May']

// Get week of month (1-4) from a date
const getWeekOfMonth = (date) => {
  const d = new Date(date)
  const day = d.getDate()
  return Math.ceil(day / 7)
}

// Get fiscal month index (0-11 where 0=June)
const getFiscalMonthIndex = (date) => {
  const d = new Date(date)
  const month = d.getMonth()
  return (month + 7) % 12
}

// Gantt View Tab - Excel-like fiscal year view
const GanttViewTab = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [schedules, setSchedules] = useState([])
  const [departments, setDepartments] = useState([])

  // Filter states - default to current fiscal year
  const currentYear = new Date().getFullYear()
  const currentMonth = new Date().getMonth()
  const defaultFiscalYear = currentMonth >= 5 ? currentYear : currentYear - 1

  const [fiscalYear, setFiscalYear] = useState(defaultFiscalYear.toString())
  const [facilityFilter, setFacilityFilter] = useState('')
  const [departmentFilter, setDepartmentFilter] = useState('')

  // Calculate date range from fiscal year
  const startDate = `${fiscalYear}-06-01`
  const endDate = `${parseInt(fiscalYear) + 1}-05-31`

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        const params = {
          start_date: startDate,
          end_date: endDate,
          page_size: 1000,
        }
        if (facilityFilter) params.facility = facilityFilter
        if (departmentFilter) params.department = departmentFilter

        const [pmRes, deptRes] = await Promise.all([
          api.get('/maintenance/pm-schedules/', { params }),
          api.get('/common/departments/', { params: { page_size: 100 } }),
        ])

        setSchedules(pmRes.data.results || pmRes.data || [])
        setDepartments(deptRes.data.results || deptRes.data || [])
      } catch (error) {
        console.error('Error fetching PM schedules:', error)
        toast.error('Failed to load PM schedules')
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [startDate, endDate, facilityFilter, departmentFilter])

  // Group schedules by asset and calculate PM schedule for each month/week
  const equipmentRows = useMemo(() => {
    const assetMap = new Map()

    schedules.forEach(schedule => {
      const assetId = schedule.asset
      if (!assetMap.has(assetId)) {
        assetMap.set(assetId, {
          id: assetId,
          name: schedule.asset_name,
          department: schedule.department_name || 'Unknown',
          unitCount: schedule.unit_count || 1,
          frequencyPerYear: schedule.frequency_per_year || 4,
          maintenanceProvider: schedule.maintenance_provider_display || 'In-house BME',
          facility: schedule.facility_name || 'UCI Main',
          weeklySchedule: Array(12).fill(null).map(() => Array(4).fill(null)),
        })
      }

      const asset = assetMap.get(assetId)
      const scheduledDate = new Date(schedule.scheduled_date)
      const scheduleStart = new Date(startDate)
      const scheduleEnd = new Date(endDate)

      if (scheduledDate >= scheduleStart && scheduledDate <= scheduleEnd) {
        const monthIndex = getFiscalMonthIndex(scheduledDate)
        const weekIndex = Math.min(getWeekOfMonth(scheduledDate) - 1, 3)

        asset.weeklySchedule[monthIndex][weekIndex] = {
          id: schedule.id,
          status: schedule.status,
          date: schedule.scheduled_date,
        }
      }
    })

    return Array.from(assetMap.values()).sort((a, b) => {
      const deptCompare = a.department.localeCompare(b.department)
      if (deptCompare !== 0) return deptCompare
      return a.name.localeCompare(b.name)
    })
  }, [schedules, startDate, endDate])

  // Summary stats
  const stats = useMemo(() => {
    const total = schedules.length
    const scheduled = schedules.filter(s => s.status === 'scheduled').length
    const completed = schedules.filter(s => s.status === 'completed').length
    const overdue = schedules.filter(s => s.status === 'overdue').length
    return { total, scheduled, completed, overdue }
  }, [schedules])

  const handlePrint = () => {
    const url = `/maintenance/pm-schedule/print?fiscalYear=${fiscalYear}&facility=${facilityFilter}&department=${departmentFilter}`
    window.open(url, '_blank')
  }

  if (loading) {
    return <LoadingSpinner message="Loading PM schedule..." />
  }

  return (
    <Box>
      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Fiscal Year</InputLabel>
          <Select
            value={fiscalYear}
            onChange={(e) => setFiscalYear(e.target.value)}
            label="Fiscal Year"
          >
            {[...Array(5)].map((_, i) => {
              const year = currentYear - 2 + i
              return (
                <MenuItem key={year} value={year.toString()}>
                  FY {year}-{(year + 1).toString().slice(-2)}
                </MenuItem>
              )
            })}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Facility</InputLabel>
          <Select
            value={facilityFilter}
            onChange={(e) => setFacilityFilter(e.target.value)}
            label="Facility"
          >
            <MenuItem value="">All Facilities</MenuItem>
            <MenuItem value="uci_main">UCI Main</MenuItem>
            <MenuItem value="gulu">Gulu Cancer Center</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Department</InputLabel>
          <Select
            value={departmentFilter}
            onChange={(e) => setDepartmentFilter(e.target.value)}
            label="Department"
          >
            <MenuItem value="">All Departments</MenuItem>
            {departments.map(dept => (
              <MenuItem key={dept.id} value={dept.id}>
                {dept.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ flex: 1 }} />

        <Button
          variant="outlined"
          startIcon={<Print />}
          onClick={handlePrint}
        >
          Print Schedule
        </Button>
      </Box>

      {/* Main Table - Excel-like Gantt */}
      <TableContainer component={Paper} variant="outlined" sx={{ overflow: 'auto', maxHeight: 'calc(100vh - 350px)' }}>
        <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', minWidth: 1400 }}>
          <TableHead>
            {/* Month Headers Row */}
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  width: 200,
                  minWidth: 200,
                  borderRight: '1px solid #ccc',
                  position: 'sticky',
                  left: 0,
                  bgcolor: '#f5f5f5',
                  zIndex: 3,
                }}
                rowSpan={2}
              >
                Medical Equipment
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  width: 120,
                  minWidth: 120,
                  maxWidth: 120,
                  borderRight: '1px solid #ccc',
                  bgcolor: '#f5f5f5',
                  wordBreak: 'break-word',
                  whiteSpace: 'normal',
                }}
                rowSpan={2}
              >
                Departments/Wards
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  width: 50,
                  textAlign: 'center',
                  borderRight: '1px solid #ccc',
                  bgcolor: '#f5f5f5',
                }}
                rowSpan={2}
              >
                # of units
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  width: 50,
                  textAlign: 'center',
                  borderRight: '1px solid #ccc',
                  bgcolor: '#f5f5f5',
                }}
                rowSpan={2}
              >
                # of times
              </TableCell>
              {/* Month Headers */}
              {FISCAL_MONTHS.map((month) => (
                <TableCell
                  key={month}
                  colSpan={4}
                  sx={{
                    fontWeight: 'bold',
                    textAlign: 'center',
                    borderRight: '1px solid #ccc',
                    borderBottom: 'none',
                    p: 0.5,
                    fontSize: '0.75rem',
                    bgcolor: '#f5f5f5',
                  }}
                >
                  {month}
                </TableCell>
              ))}
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  width: 100,
                  minWidth: 100,
                  borderLeft: '1px solid #ccc',
                  bgcolor: '#f5f5f5',
                }}
                rowSpan={2}
              >
                Action by
              </TableCell>
            </TableRow>
            {/* Week Numbers Row */}
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              {FISCAL_MONTHS.map((month) => (
                [1, 2, 3, 4].map((week) => (
                  <TableCell
                    key={`${month}-${week}`}
                    sx={{
                      fontWeight: 'bold',
                      textAlign: 'center',
                      width: 20,
                      minWidth: 20,
                      maxWidth: 20,
                      p: 0.25,
                      fontSize: '0.65rem',
                      borderRight: week === 4 ? '1px solid #ccc' : '1px solid #eee',
                      bgcolor: '#f5f5f5',
                    }}
                  >
                    {week}
                  </TableCell>
                ))
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {equipmentRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={56} sx={{ textAlign: 'center', py: 4 }}>
                  No PM schedules found for the selected period.
                </TableCell>
              </TableRow>
            ) : (
              equipmentRows.map((row, rowIdx) => (
                <TableRow key={row.id || rowIdx} sx={{ '&:hover': { bgcolor: '#f9f9f9' } }}>
                  <TableCell
                    sx={{
                      fontSize: '0.75rem',
                      borderRight: '1px solid #ccc',
                      position: 'sticky',
                      left: 0,
                      bgcolor: 'white',
                      zIndex: 1,
                    }}
                  >
                    {row.name}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: '0.75rem',
                      borderRight: '1px solid #ccc',
                      maxWidth: 120,
                      wordBreak: 'break-word',
                      whiteSpace: 'normal',
                    }}
                  >
                    {row.department}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center', fontSize: '0.75rem', borderRight: '1px solid #ccc' }}>
                    {row.unitCount}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center', fontSize: '0.75rem', borderRight: '1px solid #ccc' }}>
                    {row.frequencyPerYear}
                  </TableCell>
                  {/* Weekly Schedule Cells */}
                  {row.weeklySchedule.map((monthWeeks, monthIdx) => (
                    monthWeeks.map((weekData, weekIdx) => (
                      <TableCell
                        key={`${monthIdx}-${weekIdx}`}
                        sx={{
                          p: 0,
                          width: 20,
                          minWidth: 20,
                          maxWidth: 20,
                          height: 24,
                          bgcolor: weekData
                            ? weekData.status === 'completed'
                              ? '#10B981'
                              : weekData.status === 'overdue'
                              ? '#EF4444'
                              : '#3B82F6'
                            : 'transparent',
                          borderRight: weekIdx === 3 ? '1px solid #ccc' : '1px solid #eee',
                          cursor: weekData ? 'pointer' : 'default',
                        }}
                        title={weekData ? `${formatDate(weekData.date)} - ${weekData.status}` : ''}
                        onClick={() => weekData && navigate(`/work-orders?pm=${weekData.id}`)}
                      />
                    ))
                  ))}
                  <TableCell sx={{ fontSize: '0.7rem', borderLeft: '1px solid #ccc' }}>
                    {row.maintenanceProvider}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Legend */}
      <Box sx={{ display: 'flex', gap: 3, mt: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 16, height: 16, bgcolor: '#3B82F6', borderRadius: 0.5 }} />
          <Typography variant="caption">Scheduled</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 16, height: 16, bgcolor: '#10B981', borderRadius: 0.5 }} />
          <Typography variant="caption">Completed</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 16, height: 16, bgcolor: '#EF4444', borderRadius: 0.5 }} />
          <Typography variant="caption">Overdue</Typography>
        </Box>
      </Box>

      {/* Summary */}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="body2">
          <strong>Total Equipment:</strong> {equipmentRows.length} |
          <strong> Total PMs:</strong> {stats.total} |
          <strong> Scheduled:</strong> {stats.scheduled} |
          <strong> Completed:</strong> {stats.completed} |
          <strong> Overdue:</strong> {stats.overdue}
        </Typography>
      </Box>
    </Box>
  )
}

const PMScheduleList = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // Map tab query param to index
  const getInitialTab = () => {
    const tabParam = searchParams.get('tab')
    switch (tabParam) {
      case 'templates': return 1
      case 'gantt': return 2
      default: return 0
    }
  }

  const [activeTab, setActiveTab] = useState(getInitialTab)

  return (
    <Box>
      <PageHeader
        title="Preventive Maintenance"
        subtitle="Manage PM schedules and templates"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'PM Schedule' },
        ]}
        actions={
          <Button
            variant="outlined"
            startIcon={<Dashboard />}
            onClick={() => navigate('/pm-dashboard')}
          >
            PM Dashboard
          </Button>
        }
      />

      <Card>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{ mb: 3 }}
        >
          <Tab label="PM Schedules" icon={<Schedule />} iconPosition="start" />
          <Tab label="PM Templates" icon={<AttachMoney />} iconPosition="start" />
          <Tab label="Gantt View" icon={<ViewTimeline />} iconPosition="start" />
        </Tabs>

        {activeTab === 0 && <PMSchedulesTab />}
        {activeTab === 1 && <PMTemplatesTab />}
        {activeTab === 2 && <GanttViewTab />}
      </Card>
    </Box>
  )
}

export default PMScheduleList
